import {all, call, delay, fork, put, race, select, take} from 'redux-saga/effects';
import {eventChannel} from 'redux-saga';
import * as actions from '../actions/applicationData';
import * as api from '../../api/applicationData';
import axiosInstance from '../../api/index';
import {takeLatest} from "@redux-saga/core/effects";
import ToastService from "../../services/ToastService";
import {HistoryWrapper} from "../../services/HistoryWrapper";
import {
    clearRefreshToken,
    clearToken,
    loadRefreshToken,
    loadToken,
    loadTokenIssuedAt,
    peekToken,
    saveRefreshToken,
    saveToken
} from "../../services/TokenService";
import {AUTH_STATE_LOGGED_IN, AUTH_STATE_LOGGED_OUT} from "../reducers/applicationData";
import {
    clearDeviceIdentifier, clearLocation,
    loadDeviceIdentifier,
    loadLocation,
    saveDeviceIdentifier,
    saveLocation
} from "../../services/FirebaseService";
import Configuration from "../../configuration";
import {store} from "../../services/StoreService";

export function* tokenLogin(action) {
    const {
        doAfter,
    } = action.payload;

    const token = loadToken();

    if (!token) {
        doAfter();
        return;
    }
    // loadToken() has already put the bearer into the axios defaults, which is the
    // only thing this rehydration needs. Re-saving would also re-stamp
    // token_issued_at, making a token Core issued days ago look new on every start
    // and hiding it from the keep-alive for another staleness window.

    try {
        const meResponse = yield call(api.me);
        yield put(actions.meSuccess(meResponse.data));
        yield put(actions.loginSuccess());
        HistoryWrapper.history.push('/dashboard');

        yield call(setFirebaseMeta);
    } catch (e) {
        clearToken();
    }
    doAfter();
}

export const RefreshOutcome = Object.freeze({
    // A new access token is stored and the axios defaults carry it.
    RENEWED: 'renewed',
    // Core answered that the grant is no good. Tokens cleared, logout dispatched.
    SESSION_ENDED: 'session_ended',
    // Nothing conclusive came back. Tokens left exactly as they were.
    UNAVAILABLE: 'unavailable',
});

let inFlightRefresh = null;

/**
 * Spends the refresh token for a new access token.
 *
 * Single-flight: Core deletes the refresh token it was handed as it rotates the
 * replacement, so two refreshes in the same window race, and the loser presents a
 * grant that has already been spent, is told it is invalid, and ends a session the
 * winner had just renewed. The interceptor's `_retry` flag is per request and does
 * not help. The keep-alive made this easy to hit: on wake the Firebase node
 * re-emits and dispatches terminal/signature requests at the same moment the
 * foreground trigger fires. Callers share one attempt instead.
 */
export function refreshAccessToken() {
    if (!inFlightRefresh) {
        inFlightRefresh = performRefresh().finally(() => {
            inFlightRefresh = null;
        });
    }

    return inFlightRefresh;
}

async function performRefresh() {
    // The refresh POST must not present the stale bearer, and clearToken() is what
    // takes it out of the axios defaults. Keep the old values so an attempt that
    // never reached Core can put them back untouched, age included.
    const previousToken = peekToken();
    const previousIssuedAt = loadTokenIssuedAt();
    const startedLoggedIn = store.getState().applicationData.auth.state === AUTH_STATE_LOGGED_IN;
    clearToken();

    const postData = {
        "grant_type": "refresh_token",
        "client_id": Configuration.api.clientId,
        "refresh_token": loadRefreshToken(),
    }

    try {
        const response = await axiosInstance.post('/auth/token', postData);
        const {data} = response;

        // A logout that landed while this was in flight wins. Saving here would
        // sign a terminal that someone just logged out back in behind their back.
        // Only a session that *was* live can be ended this way: tokenLogin refreshes
        // while still logged out by design, and must not be discarded.
        const loggedOutMeanwhile = store.getState().applicationData.auth.state === AUTH_STATE_LOGGED_OUT;
        if (startedLoggedIn && loggedOutMeanwhile) {
            return RefreshOutcome.SESSION_ENDED;
        }

        saveToken(data.accessToken);
        if (data.refreshToken) {
            saveRefreshToken(data.refreshToken);
        }
        return RefreshOutcome.RENEWED;
    } catch (e) {
        // Only Core answering that the grant is bad ends the session. A request
        // that never got a response (offline, DNS, connection reset - an iPad whose
        // Wi-Fi has not reassociated yet after waking) or a 5xx says nothing about
        // the refresh token, and throwing a multi-day credential away over one is
        // exactly the full OAuth login this keep-alive exists to prevent. Put the
        // access token back and let a later attempt decide.
        const status = e?.response?.status;
        if (status !== 400 && status !== 401) {
            if (previousToken) {
                saveToken(previousToken, previousIssuedAt);
            }
            return RefreshOutcome.UNAVAILABLE;
        }

        clearRefreshToken();
        clearToken();
        // The refresh was the last chance to stay signed in. Drop to logged out
        // here rather than letting the caller retry with no token and wait for a
        // second 401 to notice. Called from the axios interceptor, outside saga
        // context, so this dispatches on the store directly.
        store.dispatch(actions.logoutSuccess());
        return RefreshOutcome.SESSION_ENDED;
    }
}

function* login(action) {
    const {
        code,
        doAfter,
    } = action.payload;
    try {
        const response = yield call(api.tokenRequest, {code});
        const {data} = response;

        if (data.accessToken) {
            saveToken(data.accessToken);
            saveRefreshToken(data.refreshToken);
            const meResponse = yield call(api.me);
            yield put(actions.meSuccess(meResponse.data));
            yield put(actions.loginSuccess());
            // ToastService.success('Successfully logged in.');
            HistoryWrapper.history.push('/dashboard');

            yield call(setFirebaseMeta);
        } else {
            clearToken();
            clearRefreshToken();
        }
    } catch (e) {
        doAfter();
        clearToken();
        clearRefreshToken();
    }
}

function* setFirebaseMeta() {
    const firebaseLocation = loadLocation();
    const firebaseDeviceIdentifier = loadDeviceIdentifier();

    if (firebaseLocation && firebaseDeviceIdentifier) {
        yield put(actions.setFirebaseMeta({
            location: firebaseLocation,
            identifier: firebaseDeviceIdentifier,
        }));
    } else {
        const registerResponse = yield call(api.registerTerminal);
        saveDeviceIdentifier(registerResponse.data.identifier);
        saveLocation(registerResponse.data.location);
        yield put(actions.setFirebaseMeta(registerResponse.data));
    }

    yield call(getTerminal);
}

function* orderUpload(action) {
    const {
        id,
        doAfter,
    } = action.payload;

    const state = yield select();
    const pictures = state.applicationData.pictureModeData.pictures;

    let pictureKeys = [];
    Object.keys(pictures).forEach((key) => {
       if (pictures[key].selected) pictureKeys.push(key);
    })

    try {
        yield all(pictureKeys.map(key => call(orderUploadSingle, {id, fileData: pictures[key].data})));
        ToastService.success('Upload successful')
    } catch (e) {
        ToastService.error('Upload failed');
        // This disables the loading indicator. We only disable it in the catch since the
        // view is destroyed if this is not caught
        doAfter();
    }
}

function* orderUploadSingle({id, fileData}) {
    try {
        yield call(api.orderUpload, {id, fileData});
        yield put(actions.orderUploadSuccess({id}));
    } catch (e) {
        ToastService.error('Upload failed');
    }
}

export const ORDER_NOT_FOUND_MESSAGE = 'Could not fetch the order. Order might not exist.';
export const ORDER_FORBIDDEN_MESSAGE = 'Your user role does not have access to this order.';

/**
 * The toast for an order that could not be fetched. A 403 is the signed-in
 * user's role refusing the order, which is not the same as the order missing.
 * Without a response the interceptor has already said the connection is lost.
 */
export function orderFetchErrorMessage(error) {
    if (error && error.response && error.response.status === 403) {
        return ORDER_FORBIDDEN_MESSAGE;
    }

    return ORDER_NOT_FOUND_MESSAGE;
}

/**
 * The customer's display name, or null.
 *
 * The order carries only a link to its customer. Following it needs
 * customer:view on the signed-in user's role, so a refusal (or any other
 * failure) leaves the name out instead of failing the order.
 */
export function* fetchCustomerName(customerRelation) {
    const match = customerRelation && customerRelation.href
        ? /\/customers\/(\d+)$/.exec(customerRelation.href)
        : null;
    if (!match) {
        return null;
    }

    try {
        const {data} = yield call(api.getCustomer, {id: match[1]});
        if (data.company) {
            return data.company;
        }

        const name = [data.firstName, data.lastName].filter(Boolean).join(' ');

        return name || null;
    } catch (e) {
        return null;
    }
}

export function* getOrder(action) {
    const {
        order // id
    } = action.payload;

    let orderResponse;
    try {
        orderResponse = yield call(api.getOrder, {id: order});
    } catch (e) {
        ToastService.error(orderFetchErrorMessage(e));
        return;
    }

    const endUser = orderResponse.data.contact ? orderResponse.data.contact.fullName : null;
    const customer = yield call(fetchCustomerName, orderResponse.data.customer);

    yield call(setOrder, {
        id: orderResponse.data.id,
        endUser: endUser,
        customer: customer,
    });
}

function* setOrder(order) {
    yield put(actions.orderSuccess(order));
}

function* clearOrder() {
    yield call(actions.orderClear);
}

function* getSignature(action) {
    const {
        identifier
    } = action.payload;

    try {
        const signatureResponse = yield call(api.getSignature, {identifier});
        yield put(actions.setSignature(signatureResponse.data));
    } catch (e) {
        yield call(clearSignature);
    }
}

function* sign(action) {
    const {
        identifier,
        fileData,
        marketingOptIn,
        additionalTermsInitials,
        doAfter,
    } = action.payload;

    try {
        yield call(api.sign, {identifier, fileData, marketingOptIn, additionalTermsInitials})
        ToastService.success('The document(s) have been signed successfully!');
        yield put(actions.setSignatureSuccessful(true));
        yield delay(10000);
        yield put(actions.setSignatureSuccessful(null));
    } catch (e) {
        yield call(clearSignature);
        ToastService.warning('Something went wrong. Please contact the staff.');
        doAfter();
    }
}

function* clearSignature() {
    yield put(actions.setSignature(null));
}

function* getTerminal() {
    const deviceIdentifier = loadDeviceIdentifier();
    try {
        let terminalResponse = yield call(api.getTerminal, {identifier: deviceIdentifier});

        const firebaseData = {
            ...terminalResponse.data,
            firebase: {
                ...Configuration.firebase,
                apiKey: terminalResponse.data.firebaseApiKey,
            },
        }

        if (firebaseData?.firebaseApiKey) {
            delete(firebaseData.firebaseApiKey);
        }

        yield put(actions.setFirebaseData(firebaseData));
    } catch (e) {
        ToastService.warning('Error connecting to Fixably.');
    }
}

function* setFirebaseSignedIn(action) {
    yield put(actions.setFirebaseSignedInSuccess(action.payload.signedIn));
}

// Unreachable: no watcher dispatches this, and nothing calls it. Kept rather than
// deleted because several Z_DOCS areas cite it by name as the recovery path that
// was designed and never wired (device-pairing README Gotchas, firebase-realtime
// README). Deleting it is the right end state, but it has to happen together with
// those docs rather than as a drive-by in a lint pass.
// eslint-disable-next-line no-unused-vars
function* clearFirebase() {
    clearDeviceIdentifier();
    clearLocation();
    yield put(actions.setFirebaseData(null));
    yield put(actions.setFirebaseMeta(null));
    ToastService.warning('The device identifier no longer exists. Device is registered again on next login. Logging out.');
    yield call(logout);
}

export function* logout() {
    clearToken();
    // The refresh token has to go too. It used to be left behind, which was a
    // small window while nothing renewed it; this change makes refresh tokens
    // long-lived by design, so a tablet signed out on a shop counter would
    // otherwise keep a credential good for days that anyone with the device
    // could spend.
    clearRefreshToken();
    yield put(actions.logoutSuccess());
}

function* receiveMode() {
    yield put(actions.receiveModeSuccess());
}

function* exitReceiveMode(action) {
    const {
        pin,
        force
    } = action.payload;

    const state = yield select();
    if (force) {
        yield put(actions.exitReceiveModeSuccess());
    } else if (pin === state.applicationData.firebaseData.unlockCode) {
        yield put(actions.exitReceiveModeSuccess());
    } else {
        ToastService.warning('Incorrect PIN', '');
    }
}

// function* pictureSave(action) {
//     const {
//         data
//     } = action;
//     yield put(actions.pictureSave({data}));
// }
//
// function* pictureRemove(action) {
//     const {
//         key,
//     } = action;
//     yield put(actions.pictureRemove({key}));
// }
//
// function* pictureToggleSelection({key}) {
//     yield put(actions.pictureToggleSelection({key}));
// }

function* pictureMode() {
    yield put(actions.pictureModeSuccess());
}

function* exitPictureMode() {
    yield put(actions.exitPictureModeSuccess());
}

// START OF WATCHERS
// function* watchPictureToggleSelection() {
//     yield takeLatest(actions.Types.PICTURE_TOGGLE_SELECTION, pictureToggleSelection);
// }
//
// function* watchPictureSave() {
//     yield takeLatest(actions.Types.PICTURE_SAVE, pictureSave);
// }
//
// function* watchPictureRemove() {
//     yield takeLatest(actions.Types.PICTURE_REMOVE, pictureRemove);
// }

function* watchSetFirebaseSignedIn() {
    yield takeLatest(actions.Types.SET_FIREBASE_SIGNED_IN, setFirebaseSignedIn);
}

function* watchOrderUploadRequest() {
    yield takeLatest(actions.Types.ORDER_UPLOAD_REQUEST, orderUpload);
}

function* watchOrderRequest() {
    yield takeLatest(actions.Types.ORDER_REQUEST, getOrder);
}

function* watchOrderClear() {
    yield takeLatest(actions.Types.ORDER_CLEAR, clearOrder);
}

function* watchSignRequest() {
    yield takeLatest(actions.Types.SIGN_REQUEST, sign);
}

function* watchClearSignature() {
    yield takeLatest(actions.Types.CLEAR_SIGNATURE, clearSignature);
}

function* watchSignatureRequest() {
    yield takeLatest(actions.Types.SIGNATURE_REQUEST, getSignature);
}

function* watchTerminalRequest() {
    yield takeLatest(actions.Types.TERMINAL_REQUEST, getTerminal);
}

function* watchTokenLogin() {
    yield takeLatest(actions.Types.TOKEN_LOGIN_REQUEST, tokenLogin);
}

function* watchLogin() {
    yield takeLatest(actions.Types.LOGIN_REQUEST, login);
}

function* watchLogout() {
    yield takeLatest(actions.Types.LOGOUT_REQUEST, logout);
}

function* watchReceiveMode() {
    yield takeLatest(actions.Types.RECEIVE_MODE_REQUEST, receiveMode);
}

function* watchExitReceiveMode() {
    yield takeLatest(actions.Types.EXIT_RECEIVE_MODE_REQUEST, exitReceiveMode);
}

function* watchPictureMode() {
    yield takeLatest(actions.Types.PICTURE_MODE_REQUEST, pictureMode);
}

function* watchExitPictureMode() {
    yield takeLatest(actions.Types.EXIT_PICTURE_MODE_REQUEST, exitPictureMode);
}

// Core issues access tokens for 1 hour and refresh tokens for 7 days, and the
// refresh token only rotates when it is spent. Nothing spends it unless a request
// takes a 401, so a terminal that goes a week without talking to Core loses its
// session and staff are sent back through the full OAuth login. Spending one on a
// schedule keeps the 7 day window rolling forward.
export const KEEPALIVE_TICK_MS = 60 * 60 * 1000;
export const KEEPALIVE_STALE_AFTER_MS = 45 * 60 * 1000;

/**
 * Emits whenever the tablet comes back to the foreground. An iPad that sleeps for
 * days runs no timers while suspended, so waking up is the moment worth checking.
 */
function foregroundChannel() {
    return eventChannel((emit) => {
        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                emit('visible');
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    });
}

export function* refreshIfStale() {
    if (!loadRefreshToken()) {
        return;
    }
    if (Date.now() - loadTokenIssuedAt() < KEEPALIVE_STALE_AFTER_MS) {
        return;
    }
    // refreshAccessToken decides what a failure means: it ends the session only
    // when Core rejects the grant and leaves the tokens untouched otherwise, so
    // waking up before the Wi-Fi has reassociated costs nothing and the next tick
    // tries again.
    yield call(refreshAccessToken);
}

function* keepSessionAlive() {
    const foreground = yield call(foregroundChannel);
    try {
        while (true) {
            yield race({
                tick: delay(KEEPALIVE_TICK_MS),
                foreground: take(foreground),
            });
            try {
                yield call(refreshIfStale);
            } catch (e) {
                // One bad tick must not end the keep-alive, and must not escape:
                // rootSaga forks this under all([...]), where an uncaught error
                // aborts every other saga in the app.
                console.error('Session keep-alive tick failed', e);
            }
        }
    } finally {
        // Closed on every exit, not just cancellation. Leaving it open on an error
        // path would strand the visibilitychange listener for the life of the page.
        foreground.close();
    }
}

function* watchSessionKeepAlive() {
    while (true) {
        yield take(actions.Types.LOGIN_SUCCESS);
        yield race({
            keepAlive: call(keepSessionAlive),
            loggedOut: take(actions.Types.LOGOUT_SUCCESS),
        });
    }
}

// END OF WATCHERS

const applicationDataSagas = [
    // fork(watchPictureToggleSelection),
    // fork(watchPictureRemove),
    // fork(watchPictureSave),
    fork(watchSetFirebaseSignedIn),
    fork(watchOrderUploadRequest),
    fork(watchOrderRequest),
    fork(watchOrderClear),
    fork(watchSignRequest),
    fork(watchClearSignature),
    fork(watchSignatureRequest),
    fork(watchTerminalRequest),
    fork(watchTokenLogin),
    fork(watchLogin),
    fork(watchLogout),
    fork(watchReceiveMode),
    fork(watchExitReceiveMode),
    fork(watchPictureMode),
    fork(watchExitPictureMode),
    fork(watchSessionKeepAlive),
];

export default applicationDataSagas;